import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PromotionCard from '../../promotion/PromotionCard';
import promotionApi from '../../../services/promotionApi';
import SectionHeader from './SectionHeader';

const SCROLL_STEP = 360;

const PromotionsSectionUpdated = () => {
  const { t } = useTranslation();
  const [promotions, setPromotions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const trackRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    promotionApi
      .getPublicPromotions({ display_format: 'section', status: 'active', limit: 15 })
      .then((data) => setPromotions(Array.isArray(data) ? data : []))
      .catch(() => setPromotions([]))
      .finally(() => setLoading(false));
  }, []);

  const updateScrollState = useCallback(() => {
    const el = trackRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 4);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  }, []);

  useEffect(() => {
    updateScrollState();
  }, [promotions, updateScrollState]);

  const scrollBy = (dir) => {
    const el = trackRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * SCROLL_STEP, behavior: 'smooth' });
  };

  const handleOrder = (promotion) => {
    if (promotion?.cta_url) {
      const ctaUrl = String(promotion.cta_url).trim();
      if (/^https?:\/\//i.test(ctaUrl)) {
        window.open(ctaUrl, '_blank', 'noopener,noreferrer');
      } else {
        navigate(ctaUrl.startsWith('/') ? ctaUrl : `/${ctaUrl}`);
      }
      return;
    }
    if (promotion.dish_ids?.length > 0) {
      navigate(`/product/${promotion.dish_ids[0]}`);
      return;
    }
    if (promotion.menu_ids?.length > 0 && promotion.restaurant_id) {
      navigate(`/restaurant/${promotion.restaurant_id}?menu=${promotion.menu_ids[0]}`);
      return;
    }
    if (promotion.restaurant_id) {
      navigate(`/restaurant/${promotion.restaurant_id}`);
    }
  };

  if (loading) {
    return (
      <div className="mt-8 mb-12">
        <SectionHeader title={t('home.specialPromotions')} />
        <div className="flex gap-5 overflow-x-auto pb-2">
          {[1, 2, 3].map((i) => (
            <div key={i} className="min-w-[320px] max-w-[320px] bg-gray-200 rounded-2xl h-64 animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  if (promotions.length === 0) return null;

  return (
    <div className="mt-8 mb-12">
      <SectionHeader
        title={t('home.specialPromotions')}
        onScrollLeft={() => scrollBy(-1)}
        onScrollRight={() => scrollBy(1)}
        canScrollLeft={canScrollLeft}
        canScrollRight={canScrollRight}
      />

      <div
        ref={trackRef}
        onScroll={updateScrollState}
        className="flex gap-5 overflow-x-auto snap-x snap-mandatory scroll-smooth pb-2"
        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
      >
        {promotions.map((promotion) => (
          <div key={promotion.id} className="snap-start shrink-0 w-[320px] sm:w-[360px]">
            <PromotionCard promotion={promotion} onOrder={handleOrder} />
          </div>
        ))}
      </div>
    </div>
  );
};

export default PromotionsSectionUpdated;
